import React, { Fragment, MouseEventHandler, ReactNode, useCallback, useEffect, useState } from 'react';
import classes from './RadioButtons.module.scss';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { ErrorRender } from '../../containers/ErrorRender';
import { FormElementAction } from '../../../models/basic-objects';
import classNames from 'classnames';
import inputClasses from '../form.module.scss';
import { capitalize } from 'lodash';

export type RadioButtonsType = {
  disabled?: boolean;
  label: string | ReactNode;
  tooltip?: ReactNode;
  value: string;
};

export type RadioButtonsViewProps = {
  //
  action?: FormElementAction;
  alignMiddle?: boolean;
  value?: string;
  disabled?: boolean;
  displayType: 'inline' | 'block';
  label?: string;
  formInstance: UseFormReturn;
  helper?: string;
  hideError?: boolean;
  id?: string;
  items: RadioButtonsType[];
  name: string;
  opaque?: boolean;
  readonly?: boolean;
  rules: RegisterOptions;
  size: 'large' | 'medium' | 'small';
  wrapper?: boolean;
};

export const RadioButtonsView = (props: RadioButtonsViewProps): JSX.Element => {
  const [executing, setExecuting] = useState<boolean>(false);
  const {
    action,
    alignMiddle,
    disabled,
    displayType,
    formInstance,
    helper,
    hideError,
    items,
    name,
    opaque,
    readonly,
    rules,
    size,
    value: inputValue,
    wrapper
  } = props;
  const {
    formState: { errors },
    getValues: getFormValues,
    register: registerForm,
    setValue: setFormValue
  } = formInstance;

  const errorClasses = classNames(inputClasses.HelpTextErrorClass, inputClasses.HelpText);
  const elemWrapperClasses = classNames(classes.RadioWrapper, classes[`RadioSize${capitalize(size)}`], {
    [classes[`RadioBlock${capitalize(size)}`]]: displayType === 'block',
    [classes.RadioBlock]: displayType === 'block'
  });
  const helperClass = classNames(inputClasses.HelpText);
  const labelClasses = classNames({
    [classes.ErrorClass]: !!errors && errors.hasOwnProperty(name),
    [classes.Opaque]: !!opaque,
    [classes.Middle]: !!alignMiddle
  });
  const componentWrapperClasses = classNames({
    [classes.RadioInline]: displayType === 'inline'
  });

  // we only use handable label click if we have provided an action
  const handleLabelClick: MouseEventHandler<HTMLLabelElement> = useCallback(
    e => {
      e.preventDefault();
      setExecuting(true);
      const currentValue = getFormValues(name);
      const target = e.target as HTMLLabelElement;
      const { value } = target.control as HTMLInputElement;
      setFormValue(name, value, { shouldValidate: true, shouldTouch: true });

      (action as FormElementAction)(value, (keepNewVal = true) => {
        if (!keepNewVal) {
          setFormValue(name, currentValue, { shouldValidate: true, shouldTouch: true });
        }
        setExecuting(false);
      });
    },
    [action, getFormValues, name, setFormValue]
  );

  const radioList: JSX.Element[] = items.map((item, index) => {
    return (
      <div key={`radio-${index}-${item.value}`} className={elemWrapperClasses}>
        <input
          {...registerForm(name, rules)}
          id={`${name}-${index}`}
          type={'radio'}
          value={item.value}
          {...(readonly || executing ? { readOnly: true } : {})}
          {...(item.disabled || disabled ? { disabled: true } : {})}
          {...(inputValue !== undefined && item.value === inputValue ? { defaultChecked: true } : {})}
        />
        <label {...(action ? { onClick: handleLabelClick } : {})} className={labelClasses} htmlFor={`${name}-${index}`}>
          {item.label}
        </label>
        {item.tooltip}
      </div>
    );
  });

  const help = helper ? <p className={helperClass}>{helper}</p> : null;

  const error: JSX.Element = hideError ? (
    <></>
  ) : (
    <ErrorRender errors={errors} name={props.name} className={errorClasses} />
  );

  let buffer = (
    <Fragment>
      <div className={componentWrapperClasses}>{radioList}</div>
      {error}
      {help}
    </Fragment>
  );

  if (wrapper) {
    buffer = <div className={'input-wrapper'}>{buffer}</div>;
  }

  // watch if default value has been changed and update
  useEffect(() => {
    //
    const currentValue = getFormValues(name);
    if (inputValue && currentValue !== inputValue) {
      setFormValue(name, inputValue);
    }
  }, [inputValue, setFormValue, getFormValues, name]);

  return <Fragment>{buffer}</Fragment>;
};
